import React, { useState } from "react";

import { Button, Col, Form, Row } from "react-bootstrap";

import { Controller, useForm } from "react-hook-form";
import { useTypedParams } from "react-router-typesafe-routes/dom";
import { ROUTES } from "src/routes";

import { ParticipantNotesFragment, useInsertParticipantNoteMutation } from "@api/graphql";
import { DetailListTable } from "@components/DetailListTable";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { FormErrorText } from "@components/form/FormErrorText";
import { Loading } from "@components/loading";

interface NotesProps {
  notes: (ParticipantNotesFragment | undefined)[];
}

export const NotesAccordion: React.FC<NotesProps> = ({ notes }) => {
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [insertParticipantNote, { loading, error }] = useInsertParticipantNoteMutation({});
  const [participantNotes, setParticipantNotes] = useState<(ParticipantNotesFragment | undefined)[]>(notes);
  const { participantId } = useTypedParams(ROUTES.Profile.AccountDetails);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { note: "" },
  });

  const noteValue = watch("note");

  const onSubmit = async (data: { note: string }) => {
    try {
      if (!participantId) {
        throw Error("Participant ID is required to insert a note.");
      }
      const res = await insertParticipantNote({
        variables: {
          participantNote: { note: data.note, participant_id: participantId },
        },
      });
      const insertParticipantNoteRes = res?.data?.insertParticipantNote;
      if (insertParticipantNoteRes?.success) {
        setParticipantNotes([
          ...participantNotes,
          {
            note: insertParticipantNoteRes.note,
            date_created: insertParticipantNoteRes.date_created,
            user_full_name: insertParticipantNoteRes.user_full_name,
            note_id: insertParticipantNoteRes.participant_note_id,
          },
        ]);
        reset();
      }
    } catch (error) {
      setToastMessage(`Error submitting note ${error}`);
      setShowToast(true);
    }
  };
  const validNotes = participantNotes.filter(
    (note): note is ParticipantNotesFragment & { note_id: number } => !!note?.note_id,
  );
  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="error" />
      {loading && <Loading />}
      <CustomAccordion
        accordionKey={"Notes"}
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Notes</h5>}
        defaultActiveKey={"Notes"}
        customHeaderStyle="py-4"
        preventCloseOnContentClick
        showBottomLine
      >
        <Row>
          <>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <Form.Control {...field} as="textarea" rows={3} placeholder="Enter your note here" />
                  )}
                />
              </Form.Group>
              {error && (
                <Row>
                  <FormErrorText error={error.message} />
                </Row>
              )}
              <Row>
                <Col className="d-flex justify-content-end">
                  <Button type="submit" variant="primary" disabled={!noteValue.trim()}>
                    Save
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
          <DetailListTable
            data={validNotes}
            sortBy={(a, b) => {
              const d1 = a.date_created ? new Date(a.date_created) : 0;
              const d2 = b.date_created ? new Date(b.date_created) : 0;
              return d1 >= d2 ? -1 : 1;
            }}
            headers={[
              {
                label: "Note",
                render: (note) => note.note || "-",
              },
              {
                label: "Changed By",
                render: (note) => `By ${note.user_full_name || "Unknown"}`,
              },
              {
                label: "Date",
                render: (note) => (note.date_created ? new Date(note.date_created).toLocaleDateString() : "-"),
              },
            ]}
            rowKey={(note) => note.note_id}
          />
        </Row>
      </CustomAccordion>
    </>
  );
};
